import {
  ComponentRef,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react";
import { RefCallBack } from "react-hook-form";
import { TextInput, TextInputSelectionChangeEvent } from "react-native";
import { focusScrollRef } from "../focusScrollRef";

export type CaretSelection = { start: number; end: number };

/**
 * Owns the controlled `selection` state for an auto-width numeric input.
 * Pass the *rendered* selection through `clampSelectionToLength` before
 * handing it to the `InputField` — see that function's doc for why.
 */
export function useCaretSelection() {
  const [selection, setSelection] = useState<CaretSelection>({
    start: 0,
    end: 0,
  });
  const inputRef = useRef<ComponentRef<typeof TextInput>>(null);

  const onSelectionChange = useCallback(
    (event: TextInputSelectionChangeEvent) =>
      setSelection(event.nativeEvent.selection),
    [],
  );

  return { selection, setSelection, onSelectionChange, inputRef };
}

/**
 * Clamps a (possibly stale) selection to the current displayed text length.
 *
 * `selection` state can outlive the text it was computed against — e.g. a
 * form reset swaps in a shorter external `value` — so render the clamped
 * value on the `InputField`, not the raw state, or the caret can point past
 * the end of the text.
 */
export function clampSelectionToLength(
  selection: CaretSelection,
  length: number,
): CaretSelection {
  const start = Math.min(selection.start, length);
  const end = Math.min(selection.end, length);
  return start === selection.start && end === selection.end
    ? selection
    : { start, end };
}

/**
 * For the wrapper `Pressable`: calling the native `.focus()` alone leaves
 * the controlled `selection` state at its last value, which defaults to
 * `{0, 0}` before the field has ever been focused — so pressing anywhere in
 * the field (e.g. the padding or the suffix) puts the caret before the
 * digits instead of after them. Set the selection to the end first.
 */
export function focusToEnd(
  inputRef: MutableRefObject<ComponentRef<typeof TextInput> | null>,
  setSelection: Dispatch<SetStateAction<CaretSelection>>,
  textLength: number,
) {
  setSelection({ start: textLength, end: textLength });
  inputRef.current?.focus();
}

/**
 * gluestack's `createInput` factory mistypes the forwarded ref as the
 * underlying TextInput's props instead of its instance, so `.focus()` is
 * missing from its type on the raw `ref` prop — this recovers the real
 * instance type while still composing react-hook-form's `focusScrollRef`.
 */
export function bindInputRef(
  inputRef: MutableRefObject<ComponentRef<typeof TextInput> | null>,
  rhfRef: RefCallBack,
) {
  return (node: unknown) => {
    inputRef.current = node as ComponentRef<typeof TextInput> | null;
    focusScrollRef(rhfRef)(node);
  };
}
